import { AppCard } from "@lib/components/AppComponents";
import { ApiParams, SendByteScannedTransaction } from "@lib/types";
import { AllCurrencies, WalletAddressType } from "@lib/types/Billing";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import dayjs from "dayjs";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useTheme } from "styled-components";
import { numberFormat } from "@lib/helpers/StringHelper";
import { Empty, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { green } from "@ant-design/colors";
import { ActionButton } from "../Buttons";
import { apiGetCustomerLeadScannedTransaction } from "@lib/api/walletSendByte";

type MonthlyRevenue = {
  month: string;
  sortKey: string; // For proper date sorting
  datetime: string;
  [key: string]: string | number | undefined; // Dynamic currency fields
};

const currencySymbols: Partial<Record<WalletAddressType, string>> = {
  [WalletAddressType.USDT]: "$",
};

const currencyColors: Partial<Record<WalletAddressType, { paid: string }>> = {
  [WalletAddressType.USDT]: {
    paid: green[6],
  },
};

type CurrencyData = {
  paid: number;
};

export default function MonthlySendbyteChart() {
  const { mode } = useTheme();
  const { messages } = useIntl();
  const params = useMemo(() => {
    return {
      limit: 1000, // Get all invoices for calculation
    } as ApiParams;
  }, []);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryFn: () => apiGetCustomerLeadScannedTransaction(params),
    queryKey: ["sendbyte-chart", JSON.stringify(params)],
    enabled: !!params,
  });
  const apiData: SendByteScannedTransaction[] = data?.data?.data || [];

  // Get unique currencies from API data
  const availableCurrencies = useMemo(() => {
    if (!apiData) return [];
    const currencies = new Set<AllCurrencies>();
    apiData.forEach((item) => {
      if (item.convertedTo) {
        currencies.add(item.convertedTo);
      }
    });
    return Array.from(currencies);
  }, [data]);

  const monthlyData = useMemo(() => {
    if (!apiData) return [];

    // Map structure: monthKey -> currency -> {total, paid}
    const monthlyMap = new Map<string, Map<AllCurrencies, CurrencyData>>();

    // Group invoices by month and currency, calculate total and paid amounts
    apiData.forEach((item) => {
      if (item.createdAt && item.convertedTo) {
        const date = dayjs(item.createdAt);
        const monthKey = date.format("YYYY-MM");

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, new Map<AllCurrencies, CurrencyData>());
        }

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, new Map<AllCurrencies, CurrencyData>());
        }

        const currencyMap = monthlyMap.get(monthKey)!;
        const currentData = currencyMap.get(item.convertedTo) || {
          paid: 0,
        };
        currencyMap.set(item.convertedTo, {
          paid: currentData.paid + Number(item.convertedAmount || 0),
        });
      }
    });

    // Convert map to array and sort by date
    const monthlyArray: MonthlyRevenue[] = Array.from(monthlyMap.entries())
      .map(([monthKey, currencyMap]) => {
        const date = dayjs(monthKey);
        const monthName = date.format("MMMM YYYY");

        const revenueData: MonthlyRevenue = {
          month: monthName,
          sortKey: monthKey,
          datetime: dayjs(date).date(15).toISOString(),
        };

        // Add total, paid and unpaid amounts for each currency dynamically
        currencyMap.forEach((data, currency) => {
          const total = Math.round(data.paid * 100) / 100;
          revenueData[`${currency}`] = total;
        });

        return revenueData;
      })
      .sort((a, b) => {
        // Sort by sortKey (YYYY-MM format) for proper chronological order
        return a.sortKey.localeCompare(b.sortKey);
      });

    return monthlyArray;
  }, [data]);

  // Prepare ApexCharts series and options
  const chartSeries = useMemo(() => {
    const series: Array<{ name: string; data: number[] }> = [];

    availableCurrencies.forEach((currency) => {
      series.push({
        name: `${currency}`,
        data: monthlyData.map((item) => Number(item[`${currency}`]) || 0),
      });
    });

    return series;
  }, [monthlyData, availableCurrencies]);

  const chartOptions: ApexOptions = useMemo(() => {
    const colors: string[] = [];

    availableCurrencies.forEach((currency) => {
      const currencyColor = currencyColors[currency] || {
        paid: "#82ca9d",
        unpaid: "#8884d8",
      };
      colors.push(currencyColor.paid, currencyColor.unpaid);
    });

    return {
      series: chartSeries,
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
        toolbar: {
          show: true,
        },
        zoom: {
          enabled: true,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 10,
          borderRadiusApplication: "end", // 'around', 'end'
          borderRadiusWhenStacked: "last", // 'all', 'last'
          dataLabels: {
            total: {
              enabled: true,
              style: {
                fontSize: "13px",
                fontWeight: 900,
                color: mode === "dark" ? "#fff" : "#000",
              },
            },
          },
        },
      },
      xaxis: {
        type: "datetime",
        categories: monthlyData.map((item) => item.datetime),
        labels: {
          rotate: -55,
          rotateAlways: true,
          trim: true,
          minHeight: 70,
          style: {
            fontSize: "11px",
          },
          formatter: (value: string) => {
            return dayjs(value).format("MMMM");
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) => {
            return numberFormat(val / 1000, 0) + "K";
          },
        },
      },
      colors: colors.length > 0 ? colors : undefined,
      tooltip: {
        y: {
          formatter: (value: number, { seriesIndex, w }) => {
            const series = w.globals.seriesNames[seriesIndex];
            if (series) {
              const currencyMatch = series.match(/(BTC|ETH|USDT)/);
              const symbol = currencyMatch
                ? currencySymbols[currencyMatch[0] as WalletAddressType]
                : "";
              return `${value.toLocaleString("tr-TR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${symbol}`;
            }
            return value.toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          },
        },
        shared: true,
        intersect: false,
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      dataLabels: {
        formatter: (val: number) => {
          return numberFormat(val / 1000, 0) + "K";
        },
      },
      fill: {
        opacity: 1,
      },
      theme: {
        mode: mode,
      },
    };
  }, [monthlyData, availableCurrencies, mode]);

  return (
    <AppCard
      title={messages["common.monthlyInvoiceChart"] as string}
      extra={
        <ActionButton
          type="refresh"
          size="small"
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          loading={isLoading || isFetching}
        />
      }
    >
      {isLoading || isFetching ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {monthlyData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height={400}
            />
          )}
        </>
      )}
    </AppCard>
  );
}
